import {
  ApplicationImportanceType,
  ApplicationImportance
} from '../types/application-importance'
import {
  GraphQLInputObjectType,
  GraphQLNonNull,
  GraphQLBoolean,
  GraphQLList,
  GraphQLID,
  GraphQLFieldConfig,
  GraphQLInt,
  GraphQLString
} from 'graphql'
import {
  organizationApplicationsSettingsType,
  OrganizationApplicationsSettings
} from '../types/organization-applications-settings'
import {
  CustomMetadata,
  customMetadataInputType
} from '../types/custom-metadata'

interface Args {
  readonly input: {
    readonly organizationID: string
    readonly settings: {
      readonly importance: ApplicationImportance
      readonly autoLicense: boolean
      readonly remediationPolicies: string[]
      readonly compliancePolicies: string[]
      readonly customMetadata: CustomMetadata
      readonly filterAppMostRecentSession: boolean
      readonly preventOnboardingFailingApplications: boolean
      readonly preventOnboardingFailingApplicationsBySession: boolean
      protectRuleEnv: string
      routeExpirationDays: number | null
    }
  }
}

interface Context {
  readonly connector: {
    readonly put: (url: string, body: any) => Promise<any>
    readonly get: (url: string) => Promise<any>
  }
}

const args = {
  input: {
    type: new GraphQLInputObjectType({
      name: 'UpdateOrganizationApplicationsSettingsArgs',
      fields: () => ({
        organizationID: {
          type: new GraphQLNonNull(GraphQLID)
        },
        settings: {
          type: new GraphQLInputObjectType({
            name: 'OrganizationApplicationsSettingsInput',
            fields: () => ({
              importance: {
                type: new GraphQLNonNull(ApplicationImportanceType)
              },
              autoLicense: {
                type: new GraphQLNonNull(GraphQLBoolean)
              },
              remediationPolicies: {
                type: new GraphQLNonNull(
                  new GraphQLList(new GraphQLNonNull(GraphQLID))
                )
              },
              compliancePolicies: {
                type: new GraphQLNonNull(
                  new GraphQLList(new GraphQLNonNull(GraphQLID))
                )
              },
              customMetadata: {
                type: new GraphQLNonNull(customMetadataInputType)
              },
              filterAppMostRecentSession: {
                type: new GraphQLNonNull(GraphQLBoolean)
              },
              preventOnboardingFailingApplications: {
                type: new GraphQLNonNull(GraphQLBoolean)
              },
              preventOnboardingFailingApplicationsBySession: {
                type: new GraphQLNonNull(GraphQLBoolean)
              },
              protectRuleEnv: {
                type: GraphQLString
              },
              routeExpirationDays: {
                type: GraphQLInt
              }
            })
          })
        }
      })
    })
  }
}

export const updateOrganizationApplicationsSettings: GraphQLFieldConfig<
  undefined,
  Context,
  Args
> = {
  type: new GraphQLNonNull(organizationApplicationsSettingsType),
  args,
  async resolve(
    _source,
    args,
    context
  ): Promise<OrganizationApplicationsSettings> {
    if (!args.input) {
      throw new Error('mutation input parameter is required')
    }

    const {
      input: {
        settings: {
          autoLicense,
          importance,
          customMetadata,
          filterAppMostRecentSession,
          preventOnboardingFailingApplications,
          preventOnboardingFailingApplicationsBySession,
          protectRuleEnv,
          routeExpirationDays,
          ...settings
        },
        organizationID
      }
    } = args

    const [
      ,
      { policies: availableCompliancePolicies },
      { policies: availableRemediationPolicies }
    ] = await Promise.all([
      context.connector.put(`${organizationID}/application/settings`, {
        importance,
        auto_license: autoLicense,
        filter_app_most_recent_session: filterAppMostRecentSession,
        prevent_onboarding_failing_applications:
          preventOnboardingFailingApplications,
        prevent_onboarding_failing_applications_by_session:
          preventOnboardingFailingApplicationsBySession,
        protect_environment_displayed_in_vulnerability_grid: protectRuleEnv,
        route_expiration_days: routeExpirationDays,
        remediation_policies: settings.remediationPolicies,
        compliance_policies: settings.compliancePolicies,
        metadata: customMetadata
          ? normalizeCustomMetadata(customMetadata)
          : undefined
      }),
      context.connector.get(`${organizationID}/policy/compliance`),
      context.connector.get(`${organizationID}/policy/remediation`)
    ])

    return {
      id: organizationID,
      importance,
      autoLicense,
      filterAppMostRecentSession,
      preventOnboardingFailingApplications,
      preventOnboardingFailingApplicationsBySession,
      protectRuleEnv,
      routeExpirationDays,
      remediationPolicies: availableRemediationPolicies
        .filter(policiesMatchingIDs(settings.remediationPolicies))
        .map(responseToPolicy),
      compliancePolicies: availableCompliancePolicies
        .filter(policiesMatchingIDs(settings.compliancePolicies))
        .map(responseToPolicy)
    }
  }
}

const responseToPolicy = ({ name, policy_id: id, enabled }) => ({
  name,
  id,
  enabled
})

const policiesMatchingIDs =
  (ids) =>
  ({ policy_id }) =>
    ids.some((id) => id === `${policy_id}`)

function normalizeCustomMetadata(metadata: CustomMetadata) {
  const { fields, existingAppAction, newAppAction } = metadata

  return {
    new_apps_action: newAppAction,
    existing_apps_action: existingAppAction,
    fields: fields.map((field) => {
      const {
        id,
        type,
        displayLabel,
        agentLabel,
        subfields,
        required,
        unique
      } = field

      return {
        id,
        type,
        required,
        unique,
        display_label: displayLabel,
        agent_label: agentLabel,
        children_types:
          !!subfields && !!subfields.length
            ? subfields.map((subfield) => {
                const {
                  agentLabel: theAgentLabel,
                  displayLabel: theDisplayLabel,
                  ...rest
                } = subfield
                return {
                  ...rest,
                  display_label: theDisplayLabel,
                  agent_label: theAgentLabel
                }
              })
            : undefined
      }
    })
  }
}
